import React from 'react';

/**
 * knz フォーマットのコア型定義。
 *
 * knz はブロック記法（`<--- name --->` 〜 `<--- !name --->`）で
 * コンポーネントを埋め込む独自リッチフォーマット。詳細は
 * `.kiro/requirements/knz/specs.md` を参照。
 */

// --- param の型 ---

/** param が取りうる値の型種別（registry でのバリデーション・補完に使用） */
export type ParamType = 'string' | 'number' | 'boolean' | 'string[]' | 'idRef[]';

/** `text` param の解釈方法 */
export type TextAs =
  | 'array' // `-項目` を配列要素として扱う（List / Checklist）
  | 'joined' // `-行` を改行結合し Markdown として解釈（Callout / Quote / Text）
  | 'raw'; // `text:` 以降〜閉じタグまでを無加工の生テキスト（CodeBlock / Mermaid）

/** registry における単一 param の定義 */
export type ParamDef = {
  /** param のキー名 */
  key: string;
  /** 値の型 */
  type: ParamType;
  /** 必須かどうか */
  required: boolean;
  /** 説明文（リファレンス画面・補完に表示） */
  description: string;
  /** 設定可能な値 / サンプル値（列挙型の選択肢、プレビュー切り替え用） */
  sampleValues?: string[];
  /** 既定値（任意 param の未指定時の値） */
  defaultValue?: string;
};

// --- コンポーネント定義（registry のエントリ） ---

/**
 * パース済みブロックツリーのノード。
 * registry のコンポーネントに props として渡される情報を保持する。
 */
export type KnzNode = {
  /** ブロック名（`<--- name --->`） */
  name: string;
  /** ブロックの id（children 参照用。未指定なら undefined） */
  id?: string;
  /** 生の param（key -> 値の文字列 or 配列）。text は textAs に応じて解決済み */
  params: Record<string, string | string[]>;
  /** 子ノード（children 参照を解決したもの） */
  children: KnzNode[];
  /** ソース上の開始行（1-indexed。marker 用） */
  line: number;
};

/** registry コンポーネントに渡す props */
export type KnzComponentProps = {
  node: KnzNode;
  /** 子ノードを描画するためのレンダラ */
  renderChildren: (nodes: KnzNode[]) => React.ReactNode;
  /** インライン Markdown（text 内トークン含む）を描画するレンダラ */
  renderInline: (text: string) => React.ReactNode;
};

/** コンポーネントのグループ（ツールバーのポップアップ・ガイドのツリー分類に使用） */
export type ComponentGroup = 'structure' | 'text' | 'data' | 'layout' | 'media' | 'prose';

/** グループの表示ラベル（順序も兼ねる） */
export const COMPONENT_GROUP_LABELS: { key: ComponentGroup; label: string }[] = [
  { key: 'structure', label: '構造' },
  { key: 'text', label: 'テキスト' },
  { key: 'data', label: 'データ' },
  { key: 'layout', label: 'レイアウト' },
  { key: 'media', label: 'メディア・図' },
  { key: 'prose', label: '読み物（明朝）' },
];

/** registry のブロックコンポーネント定義 */
export type ComponentDef = {
  /** ブロック名 */
  name: string;
  /** 所属グループ */
  group: ComponentGroup;
  /** 表示用ラベル（リファレンス画面のカタログ見出し） */
  label: string;
  /** コンポーネントの説明文 */
  description: string;
  /** param 定義の一覧 */
  params: ParamDef[];
  /** children による子ブロックを受け付けるか */
  acceptsChildren: boolean;
  /** text param の解釈方法（text を持たないなら undefined） */
  textAs?: TextAs;
  /** 描画コンポーネント */
  component: React.FC<KnzComponentProps>;
  /** 利用テンプレート（コピペ用の knz ブロック記法） */
  template: string;
  /** 実践的な使用例（ガイドの「使い方の例」に表示。省略可） */
  recipes?: ComponentRecipe[];
  /** 補足・注意（ガイドに表示。省略可） */
  notes?: string[];
};

/** コンポーネントの実践的な使用例 */
export type ComponentRecipe = {
  /** 例のタイトル */
  title: string;
  /** 例の説明 */
  description?: string;
  /** knz ソース（frontmatter なしの本文。ガイドが frontmatter を補う） */
  source: string;
};

// --- インライン要素定義 ---

/** インライン要素（`{{name:value}}`）の props */
export type KnzInlineProps = {
  /** トークンの value 部分 */
  value: string;
};

/** registry のインライン要素定義 */
export type InlineDef = {
  /** 種別名（`{{name:value}}` の name） */
  name: string;
  /** 表示用ラベル */
  label: string;
  /** 説明文 */
  description: string;
  /** value の意味の説明 */
  valueDescription: string;
  /** サンプル value */
  sampleValues?: string[];
  /** 描画コンポーネント */
  component: React.FC<KnzInlineProps>;
};

// --- パース結果・エラー ---

/** パースで検出したエラー・警告の重大度 */
export type KnzSeverity = 'error' | 'warning';

/** パースで検出した診断（Monaco marker に変換される） */
export type KnzDiagnostic = {
  severity: KnzSeverity;
  message: string;
  /** 開始行（1-indexed） */
  line: number;
  /** 開始列（1-indexed）。省略時は 1 */
  column?: number;
  /** 終了列（1-indexed）。省略時は行末 */
  endColumn?: number;
};

/** frontmatter のメタ情報 */
export type KnzFrontmatter = {
  title: string;
  /**
   * 著者（userId）の配列。1 件以上必須。
   * `author: id`（単一）でも `author:` の下に `- id` を並べる（複数）でも指定できる。
   */
  authors: string[];
  /** title / author 以外の任意項目（将来拡張用） */
  extra: Record<string, string>;
};

/** パース結果全体 */
export type KnzParseResult = {
  /** frontmatter（必須。欠落時は null + diagnostics に error） */
  frontmatter: KnzFrontmatter | null;
  /** トップレベルに描画するルートノード（案C） */
  roots: KnzNode[];
  /** 診断（エラー・警告） */
  diagnostics: KnzDiagnostic[];
  /** Error が 0 件か（ビューワーの描画可否・保存警告に使用） */
  isValid: boolean;
};

/**
 * パーサが参照する registry のインターフェース（最小限）。
 * パーサ本体は registry の中身に依存しない純粋関数として保つため、
 * 必要な照会だけをこのインターフェース経由で受け取る。
 */
export type RegistryLookup = {
  /** 指定 name のコンポーネントの textAs を返す（未登録なら undefined） */
  getTextAs: (name: string) => TextAs | undefined;
  /** name が登録済みブロックか */
  hasBlock: (name: string) => boolean;
  /** name が登録済みインライン種別か */
  hasInline: (name: string) => boolean;
};
